<script setup lang="ts">
import type { Organization } from '@models/auth/organization';
import type { User as OrganizationUser } from '@models/auth/organization/user';
import type { User } from '@models/auth/user';
import { storeToRefs } from 'pinia';
import { computed, ref, watch, nextTick } from 'vue';
import { type AccountManager } from '@models/accountManager';
import { type SearchCommentTimeline } from '@models/searchCommentTimeline';
import { type SelectionItem } from '@models/selectionItem.ts';
import SliderModal from '@components/SliderModal.vue';
import Typography from '@components/Typography.vue';
import ContentVueEditor from '@components/ContentVueEditor.vue';
import Spinner from '@components/Spinner.vue';
import type { TypedUser } from '@models/typedUser';
import {
  createSearchCommentTimeline,
  getSearchCommentTimelines,
} from '@api/searchCommentTimeline/apiSearchCommentTimeline';
import { useStreetDistrict } from '@composables/useStreetDistrict';
import { useLoading } from '@composables/useLoading';
import UserAuth from '@tools/user/auth';
import { trans, transchoice } from '@elements/translation';
import { cleanEmptyEndHTML } from '@elements/html-formatter';
import IconBellRinging from '@icons/ph_bell-ringing.svg';
import Icon from '@components/Icon.vue';
import { useSelectionItemCommentsStore } from '../../stores/useSelectionItemCommentsStore.ts';
import useSelectionItemStore from '../../stores/useSelectionItemStore';
import TimelineMessage from './TimelineMessage.vue';

const selectionItemCommentsStore = useSelectionItemCommentsStore();

const { closeModal } = selectionItemCommentsStore;
const { isOpen, currentSelectionItem } = storeToRefs(selectionItemCommentsStore);

const selectionItemStore = useSelectionItemStore();
const { addSearchCommentTimeline, updateLastCommentTimeline } = selectionItemStore;
const { accountManager, organizationSize } = storeToRefs(selectionItemStore);

const { isLoading, stopLoading, startLoading } = useLoading();

const timelineMessages = ref<SearchCommentTimeline[]>([]);

const streetDistrict = computed<string>(() => {
  if (!currentSelectionItem.value) {
    return '';
  }

  const { formattedStreetDistrict } = useStreetDistrict(currentSelectionItem.value.offer.address);

  return formattedStreetDistrict.value;
});

const companyName: string = window.COMPANY_NAME;

const accountManagerFirstname = computed<string>(() => {
  if (currentSelectionItem.value?.pipedrive_user) {
    return currentSelectionItem.value.pipedrive_user.firstname;
  }

  return accountManager.value?.firstname ?? '';
});

const message = ref<string>('');

const sendMessage = async (): Promise<void> => {
  if (!currentSelectionItem.value) {
    return;
  }

  const body = cleanEmptyEndHTML(message.value);

  const newSearchCommentTimeline = await createSearchCommentTimeline({
    searchCommentId: currentSelectionItem.value.search_comment.id,
    proposalId: currentSelectionItem.value.proposal?.id ?? null,
    selectionItemId: currentSelectionItem.value.id,
    body,
  });

  if (!newSearchCommentTimeline) {
    return;
  }

  currentSelectionItem.value.search_comment.id = newSearchCommentTimeline.search_comment.id;

  timelineMessages.value = [ ...timelineMessages.value, newSearchCommentTimeline ];

  const author = newSearchCommentTimeline.impersonated
    ? trans(
      'selection_item.comment.account_manager',
      {
        firstname: (newSearchCommentTimeline.pipedrive_user as AccountManager).firstname,
        company_name: companyName,
      },
    )
    : newSearchCommentTimeline.author.full_name;

  await addSearchCommentTimeline({ itemId: currentSelectionItem.value.id, author });
  await updateLastCommentTimeline({ itemId: currentSelectionItem.value.id, searchCommentTimeline: newSearchCommentTimeline });

  message.value = '';
};

watch(currentSelectionItem, async () => {
  if (!currentSelectionItem.value?.search_comment.id) {
    timelineMessages.value = [];
    return;
  }

  startLoading();

  timelineMessages.value = await getSearchCommentTimelines({
    searchCommentId: currentSelectionItem.value.search_comment.id,
  });

  stopLoading();
});

watch(timelineMessages, async () => {
  await nextTick();

  const slideInContent = document.querySelector('.slide-content') as HTMLDivElement;

  slideInContent.scrollTo({ top: slideInContent.scrollHeight });
});

const currentUser = computed<User>(() => UserAuth.getUser()!);
const currentOrganization = computed<Organization>(() => currentUser.value.organization);
const organizationActiveUsers = computed<OrganizationUser[]>(() => (currentOrganization.value?.active_users ?? [])
  .map((user: OrganizationUser): TypedUser => ({ ...user, type: 'user' as const } as TypedUser)));

const mentionableUsers = computed<TypedUser[]>(() => {
  const initialUsers: TypedUser[] = [ ...organizationActiveUsers.value as TypedUser[], { ...currentUser.value as unknown as TypedUser, type: 'user' } as TypedUser ];
  const users: TypedUser[] = [];

  initialUsers.forEach((user) => {
    const userId = typeof user === 'object' ? user.id : user;
    if (typeof user === 'object' && ((userId !== currentUser.value.id) || UserAuth.isImpersonated())) {
      users.push({ ...user, type: 'user' });
    }
  });

  const { unmentionablePipedriveUserIds } = storeToRefs(useSelectionItemStore());

  const proposalAccountManager: AccountManager | undefined = currentSelectionItem.value?.pipedrive_user;

  if (accountManager.value && !unmentionablePipedriveUserIds.value.includes(accountManager.value.id)) {
    users.unshift({ ...accountManager.value, type: 'manager' } as TypedUser);
  }

  if (
    proposalAccountManager
    && !unmentionablePipedriveUserIds.value.includes(proposalAccountManager.id)
    && proposalAccountManager.id !== accountManager.value?.id
  ) {
    users.unshift({ ...proposalAccountManager, type: 'manager' } as TypedUser);
  }

  return users;
});
</script>

<template>
  <SliderModal
    :is-open="isOpen"
    class="comments-slider"
    @close="closeModal"
  >
    <template #header>
      {{ streetDistrict }}
    </template>

    <template #body>
      <div
        class="comments-slider__content"
      >
        <TimelineMessage
          v-if="(currentSelectionItem as SelectionItem)?.proposal?.comment"
          :message="(currentSelectionItem as SelectionItem).proposal?.comment ?? ''"
          :date="(currentSelectionItem as SelectionItem).created_at.iso"
          :author="(currentSelectionItem as SelectionItem).pipedrive_user"
        />

        <p class="comments-slider__note">
          <Typography
            as="span"
            variant="body-small-bold"
            color="blue-darker"
          >
            {{ trans('selection_item.comments_slider.content_editor.note.review') }}
          </Typography>

          <Typography as="span" variant="body-small">
            &nbsp;{{ trans('selection_item.comments_slider.content_editor.note.about') }}
          </Typography>
        </p>

        <Spinner
          v-if="isLoading"
          class="comments-slider__spinner"
          :size="44"
        />

        <ul
          v-else
          class="comments-slider__messages"
        >
          <li
            v-for="timelineMessage in timelineMessages"
            :key="timelineMessage.id"
          >
            <TimelineMessage
              :message="timelineMessage.body"
              :author="timelineMessage.impersonated ? (timelineMessage.pipedrive_user as AccountManager) : timelineMessage.author"
              :date="timelineMessage.created_at.iso"
            />
          </li>
        </ul>
      </div>
    </template>

    <template #footer>
      <ContentVueEditor
        v-model="message"
        :placeholder="trans('selection_item.comments_slider.content_editor.placeholder')"
        show-submit
        enable-mentions
        :users="mentionableUsers"
        @submit="sendMessage"
      />

      <div class="comments-slider__notification-note">
        <Icon
          :svg="IconBellRinging"
          height="16"
          width="16"
          fill="gray-darker"
        />

        <Typography
          v-if="currentSelectionItem"
          variant="caption"
          color="gray-darker"
        >
          {{
            transchoice('selection_item.comments_slider.notification_note', {
              company_name: companyName,
              count: organizationSize,
              account_manager: accountManagerFirstname,
            })
          }}
        </Typography>
      </div>
    </template>
  </SliderModal>
</template>

<style scoped lang="scss">
@use '@styles/variables' as *;
@use '@styles/mixins' as *;

.comments-slider {
  &__content {
    padding: rem(8) rem(24) rem(24) rem(24);
    display: flex;
    flex-direction: column;
    gap: rem(24);
  }

  &__note {
    display: inline;
    text-align: center;
    margin: 0;
  }

  &__spinner {
    align-self: center;
  }

  &__messages {
    margin-top: 0;
    margin-bottom: rem(145); // Footer with content editor height
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: rem(16);
  }

  &__notification-note {
    margin-top: rem(8);
    display: flex;
    align-items: center;
    gap: rem(4);
  }
}
</style>
